import { useCallback, useEffect, useMemo } from "react";
import { useAtom } from "jotai";
import debounce from "lodash.debounce";
import { isLoadedAtom } from "../atoms";
import { appLocalStore, enableDebug } from "../services";
import { useIsPageVisible } from "../../utils/useIsPageVisible";

import { Env, defaultDevEnableDebug, env, modelVersion, isAuthEnabled } from "../../utils/environment";
import { useAuth } from "../../auth/useAuth";
import { useUpdateEditor } from "../../editorPage/atomHelpers/editorUpdate";
import { useNotifySidebarUpdate } from "../../sidebar/atoms/sidebarUpdate";

// Usually we keep this short to ensure fast load time at the cost of maybe
// loading twice. But on iOS webview, memory is limited so the cost of multiple
// loads is too high, so we increase the interval to reduce chance of loading
// twice.
export const useSyncNotes = () => {
  const isPageVisible = useIsPageVisible();
  const notifyEditorUpdate = useUpdateEditor();
  const notifySidebarUpdate = useNotifySidebarUpdate();
  const { user } = useAuth();
  const [isLoaded, setIsLoaded] = useAtom(isLoadedAtom);

  const update = useCallback(() => {
    notifyEditorUpdate();
    notifySidebarUpdate();
  }, [notifySidebarUpdate, notifyEditorUpdate]);

  const debouncedUpdate = useMemo(() => debounce(update, 500, { trailing: true }), [update]);

  useEffect(() => {
    appLocalStore.refreshEditor = debouncedUpdate;
    appLocalStore.markDataAsLoaded = () => {
      if (!isLoaded) {
        setIsLoaded(true);
        update(); //WTF
      }
    };
    return () => {
      debouncedUpdate.cancel();
      appLocalStore.markDataAsLoaded = () => {};
      appLocalStore.refreshEditor = debounce(() => {});
    };
  }, [debouncedUpdate, isLoaded, update, setIsLoaded]);
  // Load all notes right at the beginning, and keep trying until we get a
  // a successful response. If we're stuck in this state, eventually the sync
  // indicator will raise an error, prompting the user to refresh the page.
  useEffect(() => {
    if (isLoaded || !user) return;
    console.log("before loadAllData sync");
    appLocalStore.setLocalUser(user);
    appLocalStore.loadAllData(modelVersion);

    // Set debug settings from local storage. If none is set, we default to
    // "*" in develop and preview branches so we can capture all logs in
    // sentry, but don't in prod to avoid spamming the user/sentry.
    const enableAll = env === Env.preview || env === Env.dev;
    enableDebug(localStorage.getItem("debug") ?? (defaultDevEnableDebug || (enableAll ? "*" : "")));
  }, [isLoaded, user]);

  // sync on interval
  useEffect(() => {
    if (!isLoaded || !isAuthEnabled) return;
    // NB: hooks are instantiated per component, so this setInterval will run once per component as well
    // Immediately attempt the first sync without waiting the 5s
    if (isPageVisible) {
      appLocalStore.sync(modelVersion);
    }
    const inter = setInterval(() => appLocalStore.sync(modelVersion), isPageVisible ? 5_000 : 120_000);
    return () => clearInterval(inter);
  }, [isPageVisible, isLoaded]);
};
